import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { NeuralBackground } from "./NeuralBackground";
import { Logo } from "./Logo";
import { Magnetic, scrollToId } from "../../lib/ui-motion";
import { useLang } from "../../i18n";

const ease = [0.16, 1, 0.3, 1];

export const Hero = ({ ready }) => {
  const { t } = useLang();
  const h = t.hero;

  const scrollTo = (id) => scrollToId(id);

  return (
    <section id="hero" className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* neural background */}
      <div className="absolute inset-0 z-0 radial-fade">
        <NeuralBackground className="opacity-90" />
      </div>
      <div className="absolute inset-0 z-0 grid-bg radial-fade opacity-60" />
      {/* glow */}
      <div className="absolute left-1/2 top-1/3 -translate-x-1/2 h-[420px] w-[620px] rounded-full bg-flame/20 blur-[140px] pointer-events-none" />

      <motion.div
        className="relative z-10 mx-auto max-w-[1200px] px-6 text-center"
        initial="hidden"
        animate="show"
        variants={{ show: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } } }}
      >
        <motion.div
          variants={{ hidden: { scale: 0, opacity: 0 }, show: { scale: 1, opacity: 1 } }}
          transition={{ duration: 0.7, ease }}
          className="flex justify-center mb-8"
        >
          <div className="rounded-2xl glass p-3.5">
            <Logo size={52} />
          </div>
        </motion.div>

        <motion.div
          variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 mb-8"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-flame animate-pulse-glow" />
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-white/70">{h.badge}</span>
        </motion.div>

        <h1 className="font-heading font-black tracking-tighter leading-[0.92] text-[13vw] sm:text-7xl lg:text-8xl xl:text-9xl text-balance">
          {[h.line1, h.line2, h.line3].map((line, li) => (
            <span key={li} className="block overflow-hidden pb-[0.05em]">
              <motion.span
                className={`inline-block ${li === 2 ? "text-gradient-flame" : ""}`}
                variants={{ hidden: { y: "115%" }, show: { y: "0%" } }}
                transition={{ duration: 0.9, ease }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
          transition={{ duration: 0.7 }}
          className="mt-7 font-heading text-lg sm:text-xl font-bold text-white"
        >
          {h.subTitle}
        </motion.p>
        <motion.p
          variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
          transition={{ duration: 0.7 }}
          className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-white/55 leading-relaxed"
        >
          {h.sub}
        </motion.p>

        <motion.div
          variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
          transition={{ duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Magnetic>
            <button
              onClick={() => scrollTo("contact")}
              data-cursor="hover"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-flame px-7 py-3.5 font-semibold text-black hover:bg-flame-soft transition-colors glow-flame"
            >
              {h.cta1}
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Magnetic>
          <Magnetic strength={0.25}>
            <button
              onClick={() => scrollTo("projects")}
              data-cursor="hover"
              data-testid="hero-cta-secondary"
              className="rounded-full border border-white/15 px-7 py-3.5 font-semibold text-white hover:border-flame/40 hover:bg-white/5 transition-colors"
            >
              {h.cta2}
            </button>
          </Magnetic>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.25em]">{h.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
};
